import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import _ from 'lodash';
import { Actions as PAYMENTACTIONS } from '../../store/actions/paymentActions';
import AddNewCard from './AddNewCard';

import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/spinner';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { CreditCard, Trash2, Plus } from 'lucide-react';

const ListPaymentMethods = ({
  paymentMethods,
  fetchingPmth,
  stripeKey,
  defaultPaymentMethod,
  stripeCustomerId,
  getStripePaymentMethods,
  createStripeCheckoutSessionSetupBacs,
  branding,
  removePaymentMethod,
  updateStripeCustomer,
}) => {
  const stripePromise = stripeKey && loadStripe(stripeKey);
  const [addCard, setAddCard] = useState(false);
  const [defaultMethod, setDefaultMethod] = useState(defaultPaymentMethod);

  useEffect(() => {
    if (stripeCustomerId) {
      (async () => await getStripePaymentMethods())();
    }
  }, [stripeCustomerId, getStripePaymentMethods]);

  const handleSetDefault = async (id) => {
    try {
      setDefaultMethod(id);
      await updateStripeCustomer('invoice_settings', `{default_payment_method:${id}}`);
    } catch (error) {
      console.log('Error', error);
    }
  };

  const handleAddBACS = async () => {
    if(!stripeCustomerId) return;
    try {
      await createStripeCheckoutSessionSetupBacs(stripeCustomerId);
    } catch (error) {
      console.error('[handleAddBACS]', error);
    }
  };

  if (!stripeCustomerId) return null;

  return (
    <Card className='border rounded-md mt-6'>
      <CardHeader>
        <CardTitle className='uppercase text-xl font-semibold flex items-center'>Payment Methods</CardTitle>
        {(fetchingPmth || !paymentMethods) && (
          <div className='w-full flex items-center justify-center p-8'>
            <Spinner size='large' />
          </div>
        )}
      </CardHeader>
      <CardContent>
        {!fetchingPmth && paymentMethods && (
          <div>
            {Array.isArray(paymentMethods) && paymentMethods?.length === 0 && stripePromise ? (
              <Elements stripe={stripePromise}>
                <AddNewCard />
              </Elements>
            ) : (
              Array.isArray(paymentMethods) &&
              paymentMethods?.length > 0 && (
                <div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Card</TableHead>
                        <TableHead>Expiry</TableHead>
                        <TableHead className='text-right'></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paymentMethods?.map((pOpt, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <div className='uppercase flex items-center'>
                              <CreditCard size={20} strokeWidth={1.5} className='mr-2' />
                              {pOpt?.card?.brand} •••• {pOpt?.card?.last4}{' '}
                            </div>
                          </TableCell>
                          <TableCell>
                            {pOpt?.card?.exp_month}/{pOpt?.card?.exp_year}
                          </TableCell>
                          <TableCell className='text-right'>
                            <div className='flex justify-end gap-2'>
                              {pOpt?.id === (defaultMethod || defaultPaymentMethod) && (
                                <Badge
                                  size='base'
                                  variant='default'
                                  className='h-8 p-2 select-none font-semibold text-base bg-primary text-primary-foreground'>
                                  Default
                                </Badge>
                              )}
                              {pOpt?.id !== (defaultMethod || defaultPaymentMethod) && (
                                <>
                                  <Button
                                    size='base'
                                    onClick={() => handleSetDefault(pOpt?.id)}
                                    className='h-8 p-2 font-semibold text-base bg-secondary text-secondary-foreground hover:text-primary-foreground'>
                                    Set as Default
                                  </Button>
                                  <button
                                    title='Delete Payment Method'
                                    id='delete-payment-method'
                                    onClick={() => removePaymentMethod(pOpt?.id)}
                                    className='cursor-pointer text-accent hover:text-destructive duration-300'>
                                    <Trash2 size={20} strokeWidth={1.5} />
                                  </button>
                                </>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )
            )}
          </div>
        )}
      </CardContent>
      {paymentMethods && Array.isArray(paymentMethods) && paymentMethods.length > 0 && (
        <CardFooter className='flex flex-col gap-2 justify-start pt-2 pb-4'>
          {addCard && stripePromise && (
            <div className='w-full mb-4'>
              <Elements stripe={stripePromise}>
                <AddNewCard setAddCard={setAddCard} addCard={addCard} />
              </Elements>
            </div>
          )}
          <div className='flex gap-2 flex-wrap'>
            {!addCard && (
              <Button
                className='font-semibold text-base bg-secondary text-secondary-foreground hover:text-primary-foreground'
                onClick={() => setAddCard(!addCard)}>
                <Plus size={16} strokeWidth={1.5} /> Add New Card
              </Button>
            )}
            <Button
              className='font-semibold text-base bg-secondary text-secondary-foreground hover:text-primary-foreground'
              onClick={() => handleAddBACS()}>
              Add BACS direct debit
            </Button>
          </div>
        </CardFooter>
      )}
    </Card>
  );
};

const mapStateToProps = (state) => {
  const {
    stripeCustomerId,
    stripeSubscriptions,
    stripeKey,
    stripePaymentMethods: { paymentMethods, loader, defaultPaymentMethod } = {},
  } = state.payment.stripe || {};

  return {
    branding: state.branding,
    paymentMethods: paymentMethods && _.orderBy(paymentMethods, (item) => item.created && new Date(item.created), 'desc'),
    fetchingPmth: loader,
    stripeKey,
    defaultPaymentMethod,
    stripeSubscriptions,
    stripeCustomerId,
  };
};

const mapDispatchToProps = (dispatch) => ({
  getStripePaymentMethods: () => dispatch(PAYMENTACTIONS.getStripePaymentMethods()),
  removePaymentMethod: (id) => dispatch(PAYMENTACTIONS.removePaymentMethod(id)),
  updateStripeCustomer: (field, value) => dispatch(PAYMENTACTIONS.updateStripeCustomer(field, value)),
  createStripeCheckoutSessionSetupBacs: (stripeCustomerId) => dispatch(PAYMENTACTIONS.createStripeCheckoutSessionSetupBacs(stripeCustomerId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ListPaymentMethods);
